import { ThunkAction } from 'redux-thunk';

import { UserAccountId } from 'api/UserAccount/model/UserAccountId';
import { UserSessionId } from 'api/UserAccount/model/UserSessionId';

import { IAction, IActionCreatorsMapObject } from 'shared/models/IAction';
import { validateValueByFieldName } from '../utils';

import { ISlimCreateRetailerFormState, SlimCreateRetailerFormFieldName } from './reducers';

import { AddressFormFieldName } from 'shared/components/AddressForm/AddressForm';
import { IValidationInputData } from 'shared/components/ValidationInput';
import { UserAccountManagerImpl } from 'shared/lib/account/impl/UserAccountManagerImpl';
import { IAccountSessionReader } from 'shared/lib/account/interfaces/IAccountSessionReader';

export interface ISlimCreateRetailerFormStore {
    SlimCreateRetailerFormState : ISlimCreateRetailerFormState;
}

export const ActionTypes = {
    SET_FORM_FIELD_VALIDATION_DATA : 'SIGN_UP_FORM/SET_FORM_FIELD_VALIDATION_DATA',
    SET_FORM_VALIDATION_DATA : 'SIGN_UP_FORM/SET_FORM_VALIDATION_DATA',
    SET_IS_SUBMITTING : 'SIGN_UP_FORM/SET_IS_SUBMITTING',
    SET_HAS_SUCCESSFULLY_SUBMITTED : 'SIGN_UP_FORM/SET_HAS_SUCCESSFULLY_SUBMITTED',
};

export namespace SlimCreateRetailerFormActionInterfaces {
    export interface ISetFormFieldValidationData extends IAction {
        payload : {
            field : SlimCreateRetailerFormFieldName;
            value : string | boolean;
            isValid : boolean;
            errorMessage : string;
        };
    }

    export interface ISetFormValidationData extends IAction {
        payload : {
            isValid : boolean;
            errorMessage : string;
        };
    }

    export interface ISetIsSubmitting extends IAction {
        payload : {
            isSubmitting : boolean;
        };
    }

    export interface ISetHasSuccessfullySubmitted extends IAction {
        payload : {
            hasSuccessfullySubmitted : boolean;
        };
    }

    export interface ISlimCreateRetailerFormActionCreatorsMapObject extends IActionCreatorsMapObject {
        onFormFieldChange : (
            field : SlimCreateRetailerFormFieldName,
            value : string | boolean
        ) => ThunkAction<void, ISlimCreateRetailerFormStore, {services : SlimCreateRetailerFormActionInterfaces.IServices}>;
        onFormFieldBlur : (
            field : SlimCreateRetailerFormFieldName,
            value : string | boolean,
        ) => ThunkAction<void, ISlimCreateRetailerFormStore, {services : SlimCreateRetailerFormActionInterfaces.IServices}>;
        onSetHasSuccessfullySubmitted : (
            hasSuccessfullySubmitted : boolean,
        ) => ThunkAction<void, ISlimCreateRetailerFormStore, {services : SlimCreateRetailerFormActionInterfaces.IServices}>;
        onResetForm : () => ThunkAction<void, ISlimCreateRetailerFormStore, {services : SlimCreateRetailerFormActionInterfaces.IServices}>;
        onSubmit : (
            formState : ISlimCreateRetailerFormState,
        ) => ThunkAction<Promise<boolean>, ISlimCreateRetailerFormStore, {services : SlimCreateRetailerFormActionInterfaces.IServices}>;
        onSetAddressFormField : (
            fieldName : AddressFormFieldName,
            validationInputData : IValidationInputData
        ) => ThunkAction<void, ISlimCreateRetailerFormStore, {services : SlimCreateRetailerFormActionInterfaces.IServices}>;
    }

    export interface IServices {
        userAccountManager : UserAccountManagerImpl;
        userSessionReader : IAccountSessionReader<UserSessionId, UserAccountId>;
    }
}

const setFormFieldValidationData = (
    field : SlimCreateRetailerFormFieldName,
    value : string | boolean,
    isValid : boolean,
    errorMessage : string
) : SlimCreateRetailerFormActionInterfaces.ISetFormFieldValidationData => ({
    payload : {
        field,
        value,
        isValid,
        errorMessage,
    },
    type : ActionTypes.SET_FORM_FIELD_VALIDATION_DATA,
});

const setFormValidationData = (
    isValid : boolean,
    errorMessage : string
) : SlimCreateRetailerFormActionInterfaces.ISetFormValidationData => ({
    payload : {
        isValid,
        errorMessage,
    },
    type : ActionTypes.SET_FORM_VALIDATION_DATA,
});

const setIsSubmitting = (
    isSubmitting : boolean
) : SlimCreateRetailerFormActionInterfaces.ISetIsSubmitting => ({
    payload : {
        isSubmitting,
    },
    type : ActionTypes.SET_IS_SUBMITTING,
});

const setHasSuccessfullySubmitted = (
    hasSuccessfullySubmitted : boolean
) : SlimCreateRetailerFormActionInterfaces.ISetHasSuccessfullySubmitted => ({
    payload : {
        hasSuccessfullySubmitted,
    },
    type : ActionTypes.SET_HAS_SUCCESSFULLY_SUBMITTED,
});

const onFormFieldChange = (
    field : SlimCreateRetailerFormFieldName,
    value : string | boolean,
) : ThunkAction<void, ISlimCreateRetailerFormStore, {services : SlimCreateRetailerFormActionInterfaces.IServices}> => {
    return (dispatch, getState, extraArguments) : void => {
        const validationResult = validateValueByFieldName(field, value as string, null);
        dispatch(setFormFieldValidationData(field, value, validationResult.isValid, validationResult.errorMessage));
    };
};

const onFormFieldBlur = (
    field : SlimCreateRetailerFormFieldName,
    value : string | boolean,
) : ThunkAction<void, ISlimCreateRetailerFormStore, {services : SlimCreateRetailerFormActionInterfaces.IServices}> => {
    return (dispatch, getState, extraArguments) : void => {
        const validationResult = validateValueByFieldName(field, value as string, null);
        dispatch(setFormFieldValidationData(field, value, validationResult.isValid, validationResult.errorMessage));
    };
};

// this is just the validation step
const onSubmit = (
    formState : ISlimCreateRetailerFormState,
) : ThunkAction<Promise<boolean>, ISlimCreateRetailerFormStore, {services : SlimCreateRetailerFormActionInterfaces.IServices}> => {
    return (dispatch, getState, extraArguments) : Promise<boolean> => {
        const validationInputDataByFieldName = formState.validationInputDataByFieldName;
        let formIsValid = true;

        Object.keys(validationInputDataByFieldName).forEach((key) => {
            const fieldName = key as SlimCreateRetailerFormFieldName;
            const fieldValue = (validationInputDataByFieldName)[fieldName].value;

            const validationResult = validateValueByFieldName(fieldName, fieldValue, null);

            const { isValid, errorMessage } = validationResult;
            formIsValid = formIsValid && isValid;

            dispatch(setFormFieldValidationData(fieldName, fieldValue, isValid, errorMessage));
        });

        if (formIsValid) {
            dispatch(setFormValidationData(true, ''));
            dispatch(setIsSubmitting(true));

            return Promise.resolve(true);
        } else {
            dispatch(setFormValidationData(false, 'Please correctly fill in the form fields.'));
        }

        return Promise.resolve(false);
    };
};

const onSetHasSuccessfullySubmitted = (
    hasSuccessfullySubmitted : boolean,
) : ThunkAction<void, ISlimCreateRetailerFormStore, {services : SlimCreateRetailerFormActionInterfaces.IServices}> => {
    return (dispatch, getState, extraArguments) : void => {
        dispatch(setHasSuccessfullySubmitted(hasSuccessfullySubmitted));
    };
};

const onSetAddressFormField = (fieldName : AddressFormFieldName, validationInputData : IValidationInputData): ThunkAction<void, ISlimCreateRetailerFormStore, {services : SlimCreateRetailerFormActionInterfaces.IServices}> => {
    return (dispatch, getState, extraArguments) : void => {
        dispatch(setFormFieldValidationData(fieldName, validationInputData.value, validationInputData.isValid, validationInputData.errorMessage));
    };
};

const onResetForm = () : ThunkAction<void, ISlimCreateRetailerFormStore, {services : SlimCreateRetailerFormActionInterfaces.IServices}> => {
    return (dispatch, getState, extraArguments) : void => {
        const validationInputDataByFieldName = getState().SlimCreateRetailerFormState.validationInputDataByFieldName;

        Object.keys(validationInputDataByFieldName).forEach((fieldNameValue) => {
            const fieldName = fieldNameValue as SlimCreateRetailerFormFieldName;
            dispatch(setFormFieldValidationData(fieldName, '', true, ''));
        });

        dispatch(setIsSubmitting(false));
    };
};

export const SlimCreateRetailerFormActions : SlimCreateRetailerFormActionInterfaces.ISlimCreateRetailerFormActionCreatorsMapObject = {
    onFormFieldChange,
    onFormFieldBlur,
    onSetAddressFormField,
    onResetForm,
    onSetHasSuccessfullySubmitted,
    onSubmit,
};
